import { Row, Col } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { RootState } from '../../../../store';
import {
    isLengthConditionFulfilled,
    isNumberConditionFulfilled,
    isSpecialCharacterConditionFulfilled,
    isLowerCaseConditionFulfilled,
    isUpperCaseConditionFulfilled,
    isNoSpaceConditionFulfilled,
} from 'susy-common/stringUtils';
import {
    PasswordRequirement,
    RequirementStatus,
} from '../passwordRequirement/PasswordRequirement';

import './PasswordStrengthMeter.css';
import { getPasswordStrength } from '../../../../utils/utils';

interface IProps {
    password: string;
    dirty: boolean;
}

enum PasswordRequirementValues {
    LENGTH = 'length',
    NUMBER = 'number',
    SPECIAL_CHARACTERS = 'special_characters',
    LOWER_CASE = 'lower_case',
    UPPER_CASE = 'upper_case',
    NO_SPACE = 'no_space',
}

export const PasswordStrengthMeter = ({ password, dirty }: IProps) => {
    const localization = useSelector(
        (state: RootState) =>
            state.localization.localizationStrings.susy.authentication
                .passwordStrengthMeter
    );

    const generatePasswordStrengthTile = (tilePosition: number) => {
        const passwordStrength = getPasswordStrength(password);
        const isFilled = passwordStrength >= tilePosition;
        return (
            <div
                data-testid={'password-strength-tile'}
                className={`password-tile ${
                    isFilled ? 'password-tile-filled' : ''
                }`}
            ></div>
        );
    };

    const getRequirementStatus = (requirement: PasswordRequirementValues) => {
        if (!dirty) {
            return RequirementStatus.INITIAL;
        }

        let isFulfilled = false;

        switch (requirement) {
            case PasswordRequirementValues.LENGTH:
                isFulfilled = isLengthConditionFulfilled(password);
                break;
            case PasswordRequirementValues.SPECIAL_CHARACTERS:
                isFulfilled = isSpecialCharacterConditionFulfilled(password);
                break;
            case PasswordRequirementValues.NUMBER:
                isFulfilled = isNumberConditionFulfilled(password);
                break;
            case PasswordRequirementValues.LOWER_CASE:
                isFulfilled = isLowerCaseConditionFulfilled(password);
                break;
            case PasswordRequirementValues.UPPER_CASE:
                isFulfilled = isUpperCaseConditionFulfilled(password);
                break;
            case PasswordRequirementValues.NO_SPACE:
                isFulfilled = isNoSpaceConditionFulfilled(password);
                break;
        }

        return isFulfilled
            ? RequirementStatus.FULFILLED
            : RequirementStatus.NOT_FULFILLED;
    };

    return (
        <div data-testid={'password-strength-meter'}>
            <Row className={'mt-3'}>
                <Col className={'pr-0'}>{generatePasswordStrengthTile(1)}</Col>
                <Col className={'pr-0'}>{generatePasswordStrengthTile(2)}</Col>
                <Col className={'pr-0'}>{generatePasswordStrengthTile(3)}</Col>
                <Col className={'pr-0'}>{generatePasswordStrengthTile(4)}</Col>
                <Col>{generatePasswordStrengthTile(5)}</Col>
            </Row>

            <div className={'mt-3 mb-4'}>
                <PasswordRequirement
                    name={localization.minLengthRequirement}
                    status={getRequirementStatus(
                        PasswordRequirementValues.LENGTH
                    )}
                />
                <PasswordRequirement
                    name={localization.specialCharacterRequirement}
                    status={getRequirementStatus(
                        PasswordRequirementValues.SPECIAL_CHARACTERS
                    )}
                />
                <PasswordRequirement
                    name={localization.numberRequirement}
                    status={getRequirementStatus(
                        PasswordRequirementValues.NUMBER
                    )}
                />
                <PasswordRequirement
                    name={localization.lowerCaseRequirement}
                    status={getRequirementStatus(
                        PasswordRequirementValues.LOWER_CASE
                    )}
                />
                <PasswordRequirement
                    name={localization.upperCaseRequirement}
                    status={getRequirementStatus(
                        PasswordRequirementValues.UPPER_CASE
                    )}
                />
                <PasswordRequirement
                    name={localization.noSpaceRequirement}
                    status={getRequirementStatus(
                        PasswordRequirementValues.NO_SPACE
                    )}
                />
            </div>
        </div>
    );
};
